import { Component, computed, effect, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AdminButtonComponent } from './admin-button.component';
import { AdminModalComponent } from './admin-modal.component';

@Component({
  selector: 'app-admin-confirm-dialog',
  standalone: true,
  imports: [FormsModule, AdminModalComponent, AdminButtonComponent],
  template: `
    @if (open()) {
      <app-admin-modal [title]="title()" (closed)="cancel()">
        <p class="confirm-message">{{ message() }}</p>
        @if (commentRequired()) {
          <label class="confirm-comment">
            <span class="confirm-comment__label">Comment (required)</span>
            <textarea
              class="confirm-comment__input"
              rows="3"
              [(ngModel)]="comment"
              name="confirmComment"
              placeholder="Add a short note…"
            ></textarea>
          </label>
        }
        @if (error()) {
          <p class="confirm-error">{{ error() }}</p>
        }
        <div class="confirm-actions">
          <app-admin-button
            [variant]="confirmVariant()"
            type="button"
            [disabled]="busy() || !canConfirm()"
            (click)="confirm()"
          >
            {{ busy() ? busyLabel() : confirmLabel() }}
          </app-admin-button>
          <app-admin-button variant="secondary" type="button" [disabled]="busy()" (click)="cancel()">
            {{ cancelLabel() }}
          </app-admin-button>
        </div>
      </app-admin-modal>
    }
  `,
  styles: `
    .confirm-message {
      margin: 0 0 1rem;
      color: #334155;
      line-height: 1.5;
    }
    .confirm-comment {
      display: block;
      margin-bottom: 1rem;
    }
    .confirm-comment__label {
      display: block;
      margin-bottom: 0.35rem;
      font-size: 0.875rem;
      color: #334155;
    }
    .confirm-comment__input {
      width: 100%;
      border: 1px solid #e2e8f0;
      border-radius: 0.375rem;
      padding: 0.5rem 0.75rem;
      font: inherit;
      resize: vertical;
    }
    .confirm-error {
      margin: 0 0 1rem;
      color: #b91c1c;
      font-size: 0.875rem;
      font-weight: 500;
    }
    .confirm-actions {
      display: flex;
      flex-wrap: wrap;
      gap: 0.75rem;
    }
  `,
})
export class AdminConfirmDialogComponent {
  readonly open = input(false);
  readonly title = input('Confirm');
  readonly message = input('');
  readonly confirmLabel = input('Confirm');
  readonly cancelLabel = input('Cancel');
  readonly busyLabel = input('Working…');
  readonly confirmVariant = input<'primary' | 'danger'>('primary');
  readonly commentRequired = input(false);
  readonly busy = input(false);
  readonly error = input<string | null>(null);

  readonly confirmed = output<string | undefined>();
  readonly cancelled = output<void>();

  comment = '';

  constructor() {
    effect(() => {
      if (this.open()) {
        this.comment = '';
      }
    });
  }

  readonly canConfirm = computed(() => !this.commentRequired() || this.comment.trim().length > 0);

  confirm(): void {
    if (!this.canConfirm()) return;
    this.confirmed.emit(this.commentRequired() ? this.comment.trim() : undefined);
  }

  cancel(): void {
    if (this.busy()) return;
    this.comment = '';
    this.cancelled.emit();
  }
}
